import { Employee } from '@/api/apiEmployees';
import { columns } from '@/components/employees_tsx/columns';
import { EmployeeDialog } from '@/components/employees_tsx/employee-dialog';
import { EmployeeStaticCard } from '@/components/employees_tsx/employee-static-card';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import useCurrentUser from '@/components/user/useCurrentUser';
import { useEmployees } from '@/hooks/useEmployees';
import { EMPLOYEE_PAGE_ROLES, type EmployeePageRole } from '@/lib/AuthorizedRoles';
import { Plus } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';

const Employees = () => {
  // State for managing the employee dialog
  const [open, setOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | undefined>(undefined);

  // Get employees data and actions from our custom hook
  const { employees, isLoading, error, deleteEmployee, swapEmployeePosition } = useEmployees();
  const { currentUser } = useCurrentUser();

  // Dialog handlers - wrapped in useCallback to prevent unnecessary re-renders
  const handleOpenDialog = useCallback((employee?: Employee | undefined) => {
    setSelectedEmployee(employee ?? undefined);
    setOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setSelectedEmployee(undefined);
    setOpen(false);
  }, []);

  // Memoize the columns configuration
  const tableColumns = useMemo(
    () =>
      columns(
        {
          onEdit: handleOpenDialog,
          onDelete: deleteEmployee,
          onSwapPosition: async (currentRow, targetRow, direction) => {
            try {
              await swapEmployeePosition({ currentRow, targetRow, direction });
              toast.success(`Successfully swapped ${currentRow.empName}'s position ${direction}`);
            } catch (error) {
              toast.error('Failed to swap employee positions');
              console.error('Swap position error:', error);
            }
          },
        },
        currentUser?.empRole
      ),
    [handleOpenDialog, deleteEmployee, swapEmployeePosition, currentUser?.empRole]
  );

  // Error handling
  if (error) {
    return <div>Error loading employees: {error.message}</div>;
  }

  return (
    <div className="flex flex-col p-1 py-2 sm:py-0">
      {/* Stats Section  & verification View Permittion */}
      {currentUser?.empRole &&
        EMPLOYEE_PAGE_ROLES.includes(currentUser.empRole[0] as EmployeePageRole) && (
          <div>
            <EmployeeStaticCard />
          </div>
        )}

      {/* Table Section */}
      <div className="flex-1 py-0 sm:py-0">
        <div className="bg-white rounded-md shadow-sm px-1 py-3 sm:p-4">
          {/* Header Section */}
          <div className="flex items-center justify-between pb-4">
            <h1 className="text-2xl font-bold tracking-tight">Employees</h1>
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="mr-2 h-4 w-4" />
              Add Employee
            </Button>
          </div>
          <div className="">
            <DataTable columns={tableColumns} data={employees} isLoading={isLoading} compact />
          </div>
        </div>
      </div>

      {/* Dialog Section */}
      <EmployeeDialog open={open} onClose={handleCloseDialog} employee={selectedEmployee} />
    </div>
  );
};

export default Employees;
